import React, { useEffect, useMemo, useState } from 'react';
import {
  Ban,
  Plus,
  Search,
  Filter,
  RefreshCw,
  Trash2,
  Edit3,
  X,
  Save,
  Loader2,
  ShieldAlert,
  Mail,
  Phone,
  User as UserIcon,
  Calendar,
  Info,
  AlertTriangle,
  CheckCircle,
  Upload,
  Download,
  FileText,
} from 'lucide-react';
import { authFetch, authDownload, getAdminToken } from '../utils/adminAuth';
import AdminShell, { StatCard } from '../components/admin/AdminShell';

const AdminBlacklistPage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [purging, setPurging] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, tone = 'success') => {
    setToast({ message, tone });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const resp = await authFetch('/api/admin/blacklist');
      if (!resp.ok) return;
      const data = await resp.json();
      setEntries(data);
    } catch (e) {
      console.error('Failed to load blacklist', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEntries(); }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      await authDownload(
        '/api/admin/blacklist/export.csv',
        `blacklist_export_${stamp}.csv`,
      );
      showToast('Export downloaded');
    } catch (e) {
      showToast('Export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handlePurgeAutoDisapproved = async () => {
    const count = stats.disapproved + stats.terminated;
    if (count === 0) {
      showToast('No auto entries to remove', 'warning');
      return;
    }
    if (!window.confirm(
      `Remove ${count} publisher(s) that were auto-added to the blacklist ` +
      `by the old rules?\n\n` +
      `This does NOT change any publisher's status on the Publishers page. ` +
      `It only removes the legacy auto blacklist entries.\n\n` +
      `Manual entries stay untouched.`,
    )) return;
    setPurging(true);
    try {
      const resp = await authFetch('/api/admin/blacklist/purge-auto-disapproved', { method: 'POST' });
      const data = await resp.json();
      if (resp.ok) {
        showToast(`Removed ${data.deleted} legacy auto entries`);
        await fetchEntries();
      } else {
        showToast(data?.detail || 'Cleanup failed', 'error');
      }
    } catch (e) {
      showToast('Cleanup error', 'error');
    } finally {
      setPurging(false);
    }
  };

  const handleTemplateDownload = async () => {
    try {
      await authDownload(
        '/api/admin/blacklist/template.csv',
        'blacklist_template.csv',
      );
    } catch (e) {
      showToast('Template download failed', 'error');
    }
  };

  const handleDelete = async (entry) => {
    if (entry.source !== 'manual') {
      showToast('Auto entries are permanent audit records', 'warning');
      return;
    }
    if (!window.confirm(`Remove ${entry.full_name || entry.email} from the blacklist?`)) return;
    try {
      const resp = await authFetch(`/api/admin/blacklist/${entry.id}`, { method: 'DELETE' });
      if (resp.ok) {
        showToast('Entry removed');
        await fetchEntries();
      } else {
        const j = await resp.json().catch(() => ({}));
        showToast(j?.detail || 'Delete failed', 'error');
      }
    } catch (e) {
      showToast('Network error', 'error');
    }
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return entries.filter((e) => {
      if (sourceFilter !== 'all' && e.source !== sourceFilter) return false;
      if (!s) return true;
      return (
        (e.full_name || '').toLowerCase().includes(s) ||
        (e.email || '').toLowerCase().includes(s) ||
        (e.phone || '').toLowerCase().includes(s) ||
        (e.reason || '').toLowerCase().includes(s)
      );
    });
  }, [entries, search, sourceFilter]);

  const stats = useMemo(() => ({
    total: entries.length,
    manual: entries.filter((e) => e.source === 'manual').length,
    disapproved: entries.filter((e) => e.source === 'auto_disapproved').length,
    terminated: entries.filter((e) => e.source === 'auto_terminated').length,
  }), [entries]);

  const sourceBadge = (source) => {
    const map = {
      manual: { label: 'MANUAL', cls: 'bg-violet-500/15 text-violet-300 border-violet-500/30' },
      auto_disapproved: { label: 'DISAPPROVED', cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
      auto_terminated: { label: 'TERMINATED', cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
    };
    const cfg = map[source] || { label: source, cls: 'bg-slate-800 text-slate-300 border-slate-700' };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${cfg.cls}`}>
        {cfg.label}
      </span>
    );
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
      });
    } catch { return '—'; }
  };

  return (
    <AdminShell
      pageTitle="Blacklist"
      pageDescription="Centralized ledger of disapproved, terminated, and manually-blocked publishers."
      rightSlot={
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            disabled={exporting || entries.length === 0}
            className="hidden md:flex items-center gap-1.5 px-3 h-9 rounded-md text-[13px] font-medium text-slate-300 hover:text-white hover:bg-slate-800/70 border border-slate-800 hover:border-slate-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="blacklist-export-btn"
            title="Download the entire blacklist as CSV"
          >
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" strokeWidth={2.25} />}
            Export CSV
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="hidden md:flex items-center gap-1.5 px-3 h-9 rounded-md text-[13px] font-medium text-slate-300 hover:text-white hover:bg-slate-800/70 border border-slate-800 hover:border-slate-700 transition-all"
            data-testid="blacklist-import-btn"
            title="Bulk-upload a CSV of publishers to blacklist"
          >
            <Upload className="w-3.5 h-3.5" strokeWidth={2.25} />
            Import CSV
          </button>
          <button
            onClick={fetchEntries}
            className="flex items-center gap-1.5 px-3 h-9 rounded-md text-[13px] font-medium text-slate-300 hover:text-white hover:bg-slate-800/70 border border-slate-800 hover:border-slate-700 transition-all"
            data-testid="blacklist-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} strokeWidth={2.25} />
            Refresh
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 px-3.5 h-9 rounded-md text-[13px] font-semibold text-white bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 shadow-lg shadow-red-500/20 transition-all"
            data-testid="blacklist-add-btn"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
            Add to Blacklist
          </button>
        </div>
      }
    >
      {/* Explainer callout */}
      <div className="mb-6 rounded-xl border border-red-500/25 bg-gradient-to-r from-red-500/10 via-rose-500/5 to-transparent p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-red-500/15 flex items-center justify-center flex-shrink-0 ring-1 ring-red-500/30">
          <ShieldAlert className="w-5 h-5 text-red-300" strokeWidth={2.25} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-semibold text-sm">Manual-only blacklist</h3>
          <p className="text-slate-400 text-xs mt-1 leading-relaxed">
            The blacklist is a curated list you fully control. Publishers only appear here when you click <span className="text-white font-medium">&ldquo;Add to Blacklist&rdquo;</span> on their KYC page, add them <span className="text-white font-medium">manually</span>, or import a CSV.
            Disapprove and Terminate no longer auto-add anyone.
            When a new application arrives with a matching <span className="text-white font-medium">email</span> or <span className="text-white font-medium">phone</span>, they&apos;re flagged instantly with a red <span className="text-red-300 font-bold">BLOCKED</span> badge on the Publishers page.
          </p>
        </div>
      </div>

      {/* Legacy cleanup card — only shows when auto entries still exist
          (from the old auto-blacklist rules). One-click removes all
          non-manual entries without touching publisher status. */}
      {(stats.disapproved > 0 || stats.terminated > 0) && (
        <div className="mb-6 rounded-xl border border-amber-500/40 bg-gradient-to-r from-amber-500/10 to-orange-500/5 p-4 flex items-start gap-3" data-testid="legacy-cleanup-card">
          <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0 ring-1 ring-amber-500/40">
            <AlertTriangle className="w-5 h-5 text-amber-300" strokeWidth={2.25} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold text-sm">Legacy auto-added entries detected</h3>
            <p className="text-slate-300 text-xs mt-1 leading-relaxed">
              You have <span className="font-bold text-amber-300">{stats.disapproved + stats.terminated}</span> publisher{stats.disapproved + stats.terminated === 1 ? '' : 's'} that were auto-added to the blacklist by the old rules
              ({stats.disapproved} disapproved, {stats.terminated} terminated).
              The blacklist is now <span className="text-white font-medium">manual-only</span>.
              Click below to remove these legacy entries — publisher statuses on the Publishers page stay unchanged.
            </p>
            <button
              onClick={handlePurgeAutoDisapproved}
              disabled={purging}
              className="mt-3 inline-flex items-center gap-1.5 px-3.5 h-8 rounded-md text-[12px] font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 shadow-md shadow-amber-500/20 disabled:opacity-60 transition-all"
              data-testid="purge-auto-disapproved-btn"
            >
              {purging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" strokeWidth={2.25} />}
              Remove all {stats.disapproved + stats.terminated} legacy auto entries
            </button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total Entries" value={stats.total} icon={Ban} tone="danger" testId="stat-total" />
        <StatCard label="Manual" value={stats.manual} icon={UserIcon} tone="violet" testId="stat-manual" />
        <StatCard label="Disapproved" value={stats.disapproved} icon={X} tone="warning" testId="stat-disapproved" />
        <StatCard label="Terminated" value={stats.terminated} icon={AlertTriangle} tone="danger" testId="stat-terminated" />
      </div>

      {/* Search + Filter */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex-1 relative min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" strokeWidth={2} />
          <input
            type="text"
            placeholder="Search by name, email, phone, or reason…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 h-10 rounded-md bg-slate-900/60 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 text-sm transition"
            data-testid="blacklist-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-500" strokeWidth={2} />
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="bg-slate-900/60 border border-slate-800 rounded-md px-3 h-10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition"
            data-testid="blacklist-filter-select"
          >
            <option value="all">All sources</option>
            <option value="manual">Manual</option>
            <option value="auto_disapproved">Auto — Disapproved</option>
            <option value="auto_terminated">Auto — Terminated</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-slate-800/70 bg-slate-900/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">Publisher</th>
                <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">Phone</th>
                <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">Reason</th>
                <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">Source</th>
                <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">Date Added</th>
                <th className="text-right px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/70">
              {loading && (
                <tr><td colSpan={6} className="text-center py-16 text-slate-500 text-sm">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-16">
                    <div className="w-14 h-14 rounded-full bg-slate-800/50 flex items-center justify-center mx-auto mb-3 ring-1 ring-slate-700">
                      <Ban className="w-6 h-6 text-slate-500" strokeWidth={2} />
                    </div>
                    <p className="text-slate-300 font-semibold">No blacklisted publishers</p>
                    <p className="text-slate-500 text-xs mt-1">
                      {entries.length === 0
                        ? 'Add someone manually or click "Add to Blacklist" from a publisher\'s KYC page.'
                        : 'Try adjusting your search or filter.'}
                    </p>
                  </td>
                </tr>
              )}
              {!loading && filtered.map((e) => (
                <tr key={e.id} className="hover:bg-slate-800/30 transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-red-500/20 to-rose-500/20 flex items-center justify-center flex-shrink-0 text-red-300 font-semibold text-xs ring-1 ring-red-500/30">
                        {(e.full_name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || '??'}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-white text-sm" data-testid={`bl-name-${e.id}`}>{e.full_name || <span className="italic text-slate-500">Unknown</span>}</p>
                        <p className="text-xs text-slate-400 truncate max-w-[280px] flex items-center gap-1" data-testid={`bl-email-${e.id}`}>
                          <Mail className="w-3 h-3 text-slate-500" strokeWidth={2} />
                          {e.email}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    {e.phone ? (
                      <span className="inline-flex items-center gap-1.5 text-sm text-slate-300 font-mono">
                        <Phone className="w-3 h-3 text-slate-500" strokeWidth={2} />
                        {e.phone}
                      </span>
                    ) : (
                      <span className="text-slate-600 text-xs italic">not on file</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    <p className="text-sm text-slate-300 max-w-[380px] line-clamp-2" title={e.reason}>
                      {e.reason || <span className="italic text-slate-600">No reason provided</span>}
                    </p>
                  </td>
                  <td className="px-5 py-3.5">
                    {sourceBadge(e.source)}
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-xs text-slate-400 flex items-center gap-1.5">
                      <Calendar className="w-3 h-3 text-slate-500" strokeWidth={2} />
                      {formatDate(e.date_added)}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => setEditingEntry(e)}
                        className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-blue-500/20 border border-transparent hover:border-blue-500/30 transition-all"
                        title="Edit"
                        data-testid={`bl-edit-${e.id}`}
                      >
                        <Edit3 className="w-4 h-4" strokeWidth={2} />
                      </button>
                      <button
                        onClick={() => handleDelete(e)}
                        disabled={e.source !== 'manual'}
                        className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-red-500/20 border border-transparent hover:border-red-500/30 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-transparent transition-all"
                        title={e.source === 'manual' ? 'Remove from blacklist' : 'Auto entries are permanent — reactivate the linked publisher to remove'}
                        data-testid={`bl-delete-${e.id}`}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={2} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showAddModal && (
        <BlacklistFormModal
          onClose={() => setShowAddModal(false)}
          onSaved={(entry) => {
            setEntries((prev) => [entry, ...prev]);
            setShowAddModal(false);
            showToast('Added to blacklist');
          }}
          onError={(msg) => showToast(msg, 'error')}
        />
      )}

      {showImportModal && (
        <BlacklistImportModal
          onClose={() => setShowImportModal(false)}
          onImported={async (summary) => {
            setShowImportModal(false);
            showToast(
              `Import done — ${summary.imported} added, ${summary.updated} updated${summary.skipped ? `, ${summary.skipped} skipped` : ''}`,
            );
            await fetchEntries();
          }}
          onError={(msg) => showToast(msg, 'error')}
          onTemplateDownload={handleTemplateDownload}
        />
      )}

      {editingEntry && (
        <BlacklistFormModal
          entry={editingEntry}
          onClose={() => setEditingEntry(null)}
          onSaved={(updated) => {
            setEntries((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
            setEditingEntry(null);
            showToast('Entry updated');
          }}
          onError={(msg) => showToast(msg, 'error')}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-[100] pointer-events-none">
          <div
            data-testid="blacklist-toast"
            className={`pointer-events-auto flex items-center gap-2.5 px-4 py-3 rounded-lg shadow-2xl border backdrop-blur-lg ${
              toast.tone === 'error'
                ? 'bg-red-950/90 border-red-500/40 text-red-200'
                : toast.tone === 'warning'
                ? 'bg-amber-950/90 border-amber-500/40 text-amber-200'
                : 'bg-emerald-950/90 border-emerald-500/40 text-emerald-200'
            }`}
          >
            {toast.tone === 'error' ? (
              <AlertTriangle className="w-4 h-4" strokeWidth={2.25} />
            ) : toast.tone === 'warning' ? (
              <Info className="w-4 h-4" strokeWidth={2.25} />
            ) : (
              <CheckCircle className="w-4 h-4" strokeWidth={2.25} />
            )}
            <span className="text-sm font-medium">{toast.message}</span>
          </div>
        </div>
      )}
    </AdminShell>
  );
};

/** Add / Edit modal */
const BlacklistFormModal = ({ entry, onClose, onSaved, onError }) => {
  const isEdit = !!entry;
  const isManualEntry = !entry || entry.source === 'manual';
  const [form, setForm] = useState({
    full_name: entry?.full_name || '',
    email: entry?.email || '',
    phone: entry?.phone || '',
    reason: entry?.reason || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      onError?.('Email is required');
      return;
    }
    if (!form.full_name.trim()) {
      onError?.('Name is required');
      return;
    }
    setSaving(true);
    try {
      const url = isEdit
        ? `/api/admin/blacklist/${entry.id}`
        : '/api/admin/blacklist';
      // On edit, only send fields the user actually changed to avoid
      // clobbering non-editable email on auto entries.
      const payload = isEdit
        ? {
            full_name: form.full_name.trim(),
            phone: form.phone.trim(),
            reason: form.reason.trim(),
            ...(isManualEntry ? { email: form.email.trim() } : {}),
          }
        : {
            full_name: form.full_name.trim(),
            email: form.email.trim(),
            phone: form.phone.trim(),
            reason: form.reason.trim(),
          };
      const resp = await authFetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok) {
        onError?.(data?.detail || `Save failed (${resp.status})`);
        return;
      }
      onSaved?.(data);
    } catch (err) {
      onError?.('Network error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      data-testid="blacklist-form-modal"
    >
      <div className="bg-slate-900 border border-slate-800 rounded-xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-red-500/20 to-rose-500/20 flex items-center justify-center ring-1 ring-red-500/30">
              <Ban className="w-4 h-4 text-red-300" strokeWidth={2.25} />
            </div>
            <div>
              <h3 className="text-white font-semibold text-[15px]">
                {isEdit ? 'Edit blacklist entry' : 'Add to Blacklist'}
              </h3>
              <p className="text-[11px] text-slate-500">
                {isEdit && entry.source !== 'manual'
                  ? 'Auto entry — email is locked to the linked publisher record'
                  : 'Future applications matching this email or phone will be flagged'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition"
            data-testid="blacklist-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Field label="Full name" required>
            <input
              type="text"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              placeholder="e.g. John Doe"
              className="w-full h-10 px-3 rounded-md bg-slate-950/60 border border-slate-700 text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 text-sm transition"
              data-testid="blacklist-form-name"
              autoFocus
            />
          </Field>
          <Field label="Email" required>
            <input
              type="email"
              value={form.email}
              disabled={isEdit && !isManualEntry}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="e.g. john@example.com"
              className="w-full h-10 px-3 rounded-md bg-slate-950/60 border border-slate-700 text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 text-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="blacklist-form-email"
            />
          </Field>
          <Field label="Phone" hint="Any format — we normalise to digits.">
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="e.g. +1-555-000-1234"
              className="w-full h-10 px-3 rounded-md bg-slate-950/60 border border-slate-700 text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 text-sm font-mono transition"
              data-testid="blacklist-form-phone"
            />
          </Field>
          <Field label="Reason for blacklisting" hint="Shown to any admin reviewing a match.">
            <textarea
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              rows={3}
              maxLength={2000}
              placeholder="e.g. TCPA violations, fake calls, refused payout dispute…"
              className="w-full px-3 py-2.5 rounded-md bg-slate-950/60 border border-slate-700 text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 text-sm transition resize-y"
              data-testid="blacklist-form-reason"
            />
          </Field>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 h-9 rounded-md text-sm font-medium text-slate-300 border border-slate-700 hover:bg-slate-800 transition"
              data-testid="blacklist-form-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-1.5 px-5 h-9 rounded-md text-sm font-semibold text-white bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 shadow-lg shadow-red-500/20 disabled:opacity-60 transition-all"
              data-testid="blacklist-form-submit"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={2.25} />}
              {isEdit ? 'Save changes' : 'Add to Blacklist'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Field = ({ label, hint, required, children }) => (
  <label className="block">
    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-[0.14em] flex items-center gap-1.5">
      {label}
      {required && <span className="text-red-400">*</span>}
    </span>
    <div className="mt-1.5">{children}</div>
    {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
  </label>
);

/** CSV import modal — drag & drop, live preview of first rows, summary after upload */
const BlacklistImportModal = ({ onClose, onImported, onError, onTemplateDownload }) => {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [summary, setSummary] = useState(null);
  const fileInputRef = React.useRef(null);

  const handleFile = (f) => {
    if (!f) return;
    const name = (f.name || '').toLowerCase();
    if (!name.endsWith('.csv') && !(f.type || '').startsWith('text/')) {
      onError?.('Please select a .csv file');
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      onError?.('CSV must be under 2 MB');
      return;
    }
    setFile(f);
    setSummary(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = getAdminToken();
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/blacklist/import`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        },
      );
      const data = await resp.json();
      if (!resp.ok) {
        onError?.(data?.detail || `Import failed (${resp.status})`);
        return;
      }
      setSummary(data);
      if (!data.errors || data.errors.length === 0) {
        // Close after a brief moment so user sees the success flash
        setTimeout(() => onImported?.(data), 800);
      }
    } catch (e) {
      onError?.('Upload failed — network error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      data-testid="blacklist-import-modal"
    >
      <div className="bg-slate-900 border border-slate-800 rounded-xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center ring-1 ring-blue-500/30">
              <Upload className="w-4 h-4 text-blue-300" strokeWidth={2.25} />
            </div>
            <div>
              <h3 className="text-white font-semibold text-[15px]">Import blacklist CSV</h3>
              <p className="text-[11px] text-slate-500">
                Accepted columns: <span className="text-slate-300 font-mono">full_name, email, phone, reason</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition"
            data-testid="import-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {!summary && (
            <>
              {/* Drop zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  const f = e.dataTransfer?.files?.[0];
                  if (f) handleFile(f);
                }}
                className={`relative rounded-xl border-2 border-dashed p-8 text-center transition-all ${
                  dragOver
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-slate-700 bg-slate-950/40 hover:border-slate-600'
                }`}
                data-testid="import-dropzone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  data-testid="import-file-input"
                />
                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="w-8 h-8 text-blue-400" strokeWidth={1.75} />
                    <div className="text-left">
                      <p className="text-white font-semibold text-sm truncate max-w-[300px]" data-testid="import-file-name">{file.name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                      className="relative z-10 p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition"
                      data-testid="import-clear-file"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-10 h-10 mx-auto text-slate-500 mb-3" strokeWidth={1.75} />
                    <p className="text-slate-300 font-semibold text-sm">
                      Drag & drop your CSV here
                    </p>
                    <p className="text-slate-500 text-xs mt-1">or click to browse · max 2 MB</p>
                  </>
                )}
              </div>

              {/* Template link */}
              <div className="flex items-start gap-2.5 rounded-lg bg-blue-500/10 border border-blue-500/25 px-3.5 py-3">
                <Info className="w-4 h-4 text-blue-300 flex-shrink-0 mt-0.5" strokeWidth={2.25} />
                <div className="flex-1 min-w-0 text-xs text-slate-300 leading-relaxed">
                  <p>Only <span className="font-semibold text-white">email</span> is required per row. Aliases like <span className="font-mono text-slate-400">name / e-mail / mobile / notes</span> are auto-detected. Duplicate emails update the existing row.</p>
                  <button
                    type="button"
                    onClick={onTemplateDownload}
                    className="mt-2 inline-flex items-center gap-1.5 text-blue-300 hover:text-blue-200 font-medium underline underline-offset-2"
                    data-testid="import-template-download"
                  >
                    <Download className="w-3 h-3" strokeWidth={2.25} />
                    Download CSV template
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 h-9 rounded-md text-sm font-medium text-slate-300 border border-slate-700 hover:bg-slate-800 transition"
                  data-testid="import-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={!file || uploading}
                  className="flex items-center gap-1.5 px-5 h-9 rounded-md text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-lg shadow-blue-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  data-testid="import-submit"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={2.25} />}
                  {uploading ? 'Uploading…' : 'Upload CSV'}
                </button>
              </div>
            </>
          )}

          {summary && (
            <div className="space-y-4" data-testid="import-summary">
              <div className="text-center py-4">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-emerald-500/15 flex items-center justify-center ring-1 ring-emerald-500/40">
                  <CheckCircle className="w-7 h-7 text-emerald-400" strokeWidth={2.25} />
                </div>
                <h4 className="text-white font-semibold text-lg">Import processed</h4>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <SummaryStat label="Added" value={summary.imported} tone="success" />
                <SummaryStat label="Updated" value={summary.updated} tone="info" />
                <SummaryStat label="Skipped" value={summary.skipped} tone="warning" />
              </div>

              {summary.errors && summary.errors.length > 0 && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
                  <p className="text-xs font-semibold text-amber-300 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2.25} />
                    Rows skipped ({summary.errors.length})
                  </p>
                  <ul className="text-xs text-slate-300 space-y-1 max-h-40 overflow-y-auto">
                    {summary.errors.map((e, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-slate-500 font-mono flex-shrink-0">
                          {e.row ? `#${e.row}` : '—'}
                        </span>
                        <span className="text-slate-400 flex-1 min-w-0">
                          <span className="text-slate-300">{e.email || '(blank)'}</span>
                          {' — '}
                          <span className="text-amber-300">{e.error}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex items-center justify-end pt-1">
                <button
                  type="button"
                  onClick={() => onImported?.(summary)}
                  className="flex items-center gap-1.5 px-5 h-9 rounded-md text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-lg shadow-blue-500/20 transition-all"
                  data-testid="import-done"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SummaryStat = ({ label, value, tone = 'neutral' }) => {
  const tones = {
    success: 'text-emerald-300 ring-emerald-500/30 bg-emerald-500/10',
    info: 'text-blue-300 ring-blue-500/30 bg-blue-500/10',
    warning: 'text-amber-300 ring-amber-500/30 bg-amber-500/10',
  };
  return (
    <div className={`rounded-lg px-3 py-2.5 ring-1 ${tones[tone] || tones.info}`}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] opacity-70">{label}</p>
      <p className="text-2xl font-bold tabular-nums mt-0.5">{value ?? 0}</p>
    </div>
  );
};

export default AdminBlacklistPage;
